import type {
  ClicktermDialogConfig,
  ClicktermDialogListeners,
  ClicktermDialogMode,
  ClicktermInlineCheckboxCheckedStyleOptions,
  ClicktermInlineCheckboxDisabledStyleOptions,
  ClicktermInlineCheckboxFocusStyleOptions,
  ClicktermInlineCheckboxStateStyleOptions,
  ClicktermInlineFinalizeAllResult,
  ClicktermInlineCheckboxStyleOptions,
  ClicktermInlineCheckboxVerticalAlign,
  ClicktermInlineContainerStyleOptions,
  ClicktermInlineDimension,
  ClicktermInlineErrorInfo,
  ClicktermInlineFinalizeResult,
  ClicktermInlineHandle,
  ClicktermInlineLoadingInfo,
  ClicktermInlineLockedStyleOptions,
  ClicktermInlineOptions,
  ClicktermInlineOutcome,
  ClicktermInlineReadyInfo,
  ClicktermInlineStyleIssue,
  ClicktermInlineStyleIssueReason,
  ClicktermInlineStyleOptions,
  ClicktermInlineTextBoldMarkStyleOptions,
  ClicktermInlineTextDecorationMarkStyleOptions,
  ClicktermInlineTextHeadingStyleOptions,
  ClicktermInlineTextLinkStateStyleOptions,
  ClicktermInlineTextLinkStyleOptions,
  ClicktermInlineTextMarkPaintStyleOptions,
  ClicktermInlineTextMarksStyleOptions,
  ClicktermInlineTextStyleOptions,
  ClickwrapAcceptedAgreementRequest,
  ClickwrapEvent,
  ClickwrapResult,
  ClickwrapSignatureResponse,
  ClickwrapTemplateContent,
  ClickwrapTemplateRequest,
  ClickwrapTemplateResponse,
  ClientConfiguration,
  InteractionType,
} from './models';
import { invokeInlineLifecycleCallback } from './inline/lifecycle';
import { startTranslationsLoad } from './translations';
import { ClickwrapError } from './utils';
import { Widget } from './widget';

const defaultBaseUrl = 'https://api.clickterm.com';

export class ClicktermClient {
  private static configuration: ClientConfiguration | null = null;

  static initialize(appId: string, baseUrl: string = defaultBaseUrl): void {
    if (!appId) {
      throw new ClickwrapError('AppId is required');
    }
    this.configuration = { appId, baseUrl };
    startTranslationsLoad(baseUrl);
  }

  static getConfiguration(): ClientConfiguration {
    if (!this.configuration) {
      throw new ClickwrapError('ClicktermClient has not been initialized. Call initialize() first.');
    }
    return this.configuration;
  }
}

export class ClicktermDialog {
  static async show(
    clicktermTemplateRequest: ClickwrapTemplateRequest,
    clicktermDialogConfig: ClicktermDialogConfig = {},
    listeners: ClicktermDialogListeners = {}
  ): Promise<ClickwrapResult> {
    const { appId, baseUrl } = ClicktermClient.getConfiguration();
    const widget = new Widget(appId, baseUrl);
    let result: ClickwrapResult | null = null;
    if (clicktermDialogConfig === null || listeners === null) {
      console.error("The SDK cannot accept null for ClicktermDialogOptions or ClicktermDialogListeners parameters.");
      return Promise.reject(new ClickwrapError("dialogOptions and listeners parameters cannot be null."));
    }
    try {
      result = await widget.clickwrap({ ...clicktermTemplateRequest, interactionType: 'DIALOG' }, clicktermDialogConfig);
      if (result.isAlreadyAccepted) {
        listeners.onAlreadyAccepted?.();
      } else if (result.isCanceled) {
        listeners.onCancel?.();
      } else if (result.clicktermSignature) {
        listeners.onSuccess?.(result.clicktermSignature);
      }
      return result;
    } catch (error) {
      const normalizedError = error instanceof Error
        ? error
        : new ClickwrapError('Failed to display clickwrap agreement', error);
      listeners.onError?.(normalizedError);
      throw normalizedError;
    } finally {
      listeners.onComplete?.(
        result ?? { clicktermSignature: null, isAlreadyAccepted: false, isCanceled: true }
      );
    }
  }

  static async showAcceptedContent(
    clickwrapAcceptedAgreementRequest: ClickwrapAcceptedAgreementRequest,
    clicktermDialogConfig: ClicktermDialogConfig = {}
  ): Promise<ClickwrapTemplateContent> {
    const { appId, baseUrl } = ClicktermClient.getConfiguration();
    const widget = new Widget(appId, baseUrl);
    if (clicktermDialogConfig === null) {
      console.error("The SDK cannot accept null for ClicktermDialogOptions parameter.");
      return Promise.reject(new ClickwrapError("dialogOptions parameter cannot be null."));
    }
    try {
      return await widget.acceptedContent(clickwrapAcceptedAgreementRequest, clicktermDialogConfig);
    } catch (error) {
      const normalizedError = error instanceof Error
        ? error
        : new ClickwrapError('Failed to display accepted clickwrap content', error);
      throw normalizedError;
    }
  }
}

export class ClicktermDom {
  static async renderInline(
    containerId: string,
    clicktermTemplateRequest: ClickwrapTemplateRequest,
    options: ClicktermInlineOptions = {}
  ): Promise<ClicktermInlineHandle> {
    if (options === null) {
      console.error("The SDK cannot accept null for ClicktermInlineOptions parameter.");
      return Promise.reject(new ClickwrapError("inlineOptions parameter cannot be null."));
    }
    try {
      const { appId, baseUrl } = ClicktermClient.getConfiguration();
      const widget = new Widget(appId, baseUrl);
      return await widget.renderInline(containerId, { ...clicktermTemplateRequest, interactionType: 'INLINE' }, options);
    } catch (error) {
      const normalizedError = error instanceof Error
        ? error
        : new ClickwrapError('Failed to render inline clickwrap agreement', error);
      invokeInlineLifecycleCallback('onError', () => options.onError?.(normalizedError, {
        containerId: typeof containerId === 'string' ? containerId.trim() : '',
      }));
      throw normalizedError;
    }
  }

  static async finalizeAll(containerIds?: string[]): Promise<ClicktermInlineFinalizeAllResult> {
    const { appId, baseUrl } = ClicktermClient.getConfiguration();
    const widget = new Widget(appId, baseUrl);
    try {
      return await widget.finalizeAll(containerIds);
    } catch (error) {
      const normalizedError = error instanceof Error
        ? error
        : new ClickwrapError('Failed to finalize inline clickwrap agreements', error);
      throw normalizedError;
    }
  }
}

export type {
  ClicktermDialogConfig, ClicktermDialogListeners, ClicktermDialogMode,
  ClicktermInlineCheckboxCheckedStyleOptions, ClicktermInlineCheckboxDisabledStyleOptions,
  ClicktermInlineCheckboxFocusStyleOptions, ClicktermInlineCheckboxStateStyleOptions,
  ClicktermInlineCheckboxStyleOptions, ClicktermInlineCheckboxVerticalAlign,
  ClicktermInlineContainerStyleOptions, ClicktermInlineDimension,
  ClicktermInlineErrorInfo,
  ClicktermInlineFinalizeAllResult, ClicktermInlineFinalizeResult, ClicktermInlineHandle,
  ClicktermInlineLoadingInfo,
  ClicktermInlineLockedStyleOptions, ClicktermInlineOptions,
  ClicktermInlineOutcome, ClicktermInlineReadyInfo, ClicktermInlineStyleIssue,
  ClicktermInlineStyleIssueReason, ClicktermInlineStyleOptions, ClicktermInlineTextBoldMarkStyleOptions,
  ClicktermInlineTextDecorationMarkStyleOptions, ClicktermInlineTextHeadingStyleOptions,
  ClicktermInlineTextLinkStateStyleOptions, ClicktermInlineTextLinkStyleOptions,
  ClicktermInlineTextMarkPaintStyleOptions, ClicktermInlineTextMarksStyleOptions,
  ClicktermInlineTextStyleOptions,
  ClickwrapAcceptedAgreementRequest, ClickwrapEvent, ClickwrapResult,
  ClickwrapSignatureResponse,
  ClickwrapTemplateContent,
  ClickwrapTemplateRequest,
  ClickwrapTemplateResponse, ClientConfiguration, InteractionType
};

export type ClicktermClientApi = typeof ClicktermClient;
export type ClicktermDialogApi = typeof ClicktermDialog;
export type ClicktermDomApi = typeof ClicktermDom;

export interface ClicktermSDK {
  ClicktermClient?: ClicktermClientApi;
  ClicktermDialog?: ClicktermDialogApi;
  ClicktermDom?: ClicktermDomApi;
}

declare global {
  interface Window {
    Clickterm?: ClicktermSDK;
  }
}
